import { useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { DTOProfileImageInfo } from 'src/apis/dto/DTOProfileImageInfo';
import { swap } from 'src/components/react-grid-drag';
import { ProfileQueryKeys } from 'src/hooks/useProfileQuery';
import {
  deleteProfileImageAPI,
  orderProfileImagesAPI,
  updateMainProfileImageAPI,
  uploadProfileImageAPI,
} from 'src/stores/profile/apis';
import { uploadContentManager } from 'src/utils/content-manager';

export type LocalProfileImage =
  | { type: 'server'; id: number; profileImageUrl: string }
  | { type: 'local'; tempId: string; file: File; previewUrl: string; isMainReplacement?: boolean };

interface UseLocalProfileImagesOptions {
  initialImages: DTOProfileImageInfo[];
}

const arraysEqual = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false;
  return a.every((val, i) => val === b[i]);
};

export const useLocalProfileImages = ({ initialImages }: UseLocalProfileImagesOptions) => {
  const [localImages, setLocalImages] = useState<LocalProfileImage[]>(() =>
    initialImages.map((img) => ({
      type: 'server' as const,
      id: img.id,
      profileImageUrl: img.profileImageUrl,
    }))
  );
  const [deletedServerIds, setDeletedServerIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // useCallback 클로저에서 항상 최신 상태를 참조하기 위해 ref 사용
  const localImagesRef = useRef<LocalProfileImage[]>([]);
  localImagesRef.current = localImages;

  const deletedServerIdsRef = useRef<number[]>([]);
  deletedServerIdsRef.current = deletedServerIds;

  const queryClient = useQueryClient();

  const add = useCallback((file: File) => {
    const previewUrl = URL.createObjectURL(file);
    const tempId = `local-${Date.now()}`;
    setLocalImages((prev) => [...prev, { type: 'local', tempId, file, previewUrl }]);
  }, []);

  const remove = useCallback((image: LocalProfileImage) => {
    if (image.type === 'local') {
      URL.revokeObjectURL(image.previewUrl);
    }
    setLocalImages((prev) => prev.filter((img) => img !== image));
    if (image.type === 'server') {
      setDeletedServerIds((prev) => [...prev, image.id]);
    }
  }, []);

  const swapImages = useCallback((sourceIndex: number, targetIndex: number) => {
    setLocalImages((prev) => swap(prev, sourceIndex, targetIndex));
  }, []);

  const changeMain = useCallback((file: File) => {
    const previewUrl = URL.createObjectURL(file);
    const tempId = `local-${Date.now()}`;
    const currentMain = localImagesRef.current[0];

    if (currentMain?.type === 'local') {
      URL.revokeObjectURL(currentMain.previewUrl);
    }

    setLocalImages((prev) => {
      const rest = prev.slice(1);
      return [{ type: 'local', tempId, file, previewUrl, isMainReplacement: true }, ...rest];
    });
  }, []);

  const submit = useCallback(async () => {
    setIsSaving(true);

    try {
      const originalOrder = initialImages.map((img) => img.id);
      const currentDeletedIds = [...deletedServerIdsRef.current];
      const processedDeletedIds: number[] = [];
      // submit 시작 시점의 이미지 상태 스냅샷을 캡처하여
      // 비동기 작업 중 상태 변경으로 인한 불일치 방지
      const snapshotImages = [...localImagesRef.current];

      const firstImage = snapshotImages[0];
      const isMainReplacement =
        firstImage?.type === 'local' && firstImage.isMainReplacement === true;

      if (isMainReplacement) {
        const fileInfo = await uploadContentManager(firstImage.file);
        const { data } = await updateMainProfileImageAPI(fileInfo);
        const newMainImage = data[0];

        URL.revokeObjectURL(firstImage.previewUrl);
        setLocalImages((prev) =>
          prev.map((prevImg) =>
            prevImg.type === 'local' && prevImg.tempId === firstImage.tempId
              ? {
                  type: 'server',
                  id: newMainImage.id,
                  profileImageUrl: newMainImage.profileImageUrl,
                }
              : prevImg
          )
        );

        snapshotImages[0] = {
          type: 'server',
          id: newMainImage.id,
          profileImageUrl: newMainImage.profileImageUrl,
        } satisfies LocalProfileImage;
      }

      for (const id of currentDeletedIds) {
        await deleteProfileImageAPI(id);
        processedDeletedIds.push(id);
        setDeletedServerIds((prev) => prev.filter((prevId) => prevId !== id));
      }

      const tempIdToServerId = new Map<string, number>();
      for (const img of snapshotImages) {
        if (img.type === 'local') {
          const fileInfo = await uploadContentManager(img.file);
          const { data } = await uploadProfileImageAPI(fileInfo);
          const newImageId = data[data.length - 1].id;
          const newImageUrl = data[data.length - 1].profileImageUrl;
          tempIdToServerId.set(img.tempId, newImageId);

          URL.revokeObjectURL(img.previewUrl);
          setLocalImages((prev) =>
            prev.map((prevImg) =>
              prevImg.type === 'local' && prevImg.tempId === img.tempId
                ? { type: 'server', id: newImageId, profileImageUrl: newImageUrl }
                : prevImg
            )
          );
        }
      }

      const finalOrder = snapshotImages.map((img) => {
        if (img.type === 'server') return img.id;
        const serverId = tempIdToServerId.get(img.tempId);
        if (serverId === undefined) {
          throw new Error(`Server ID not found for tempId: ${img.tempId}`);
        }
        return serverId;
      });

      const hasNewImages = tempIdToServerId.size > 0;
      const hasDeletedImages = processedDeletedIds.length > 0;
      const orderChanged = !arraysEqual(originalOrder, finalOrder);

      if (hasNewImages || hasDeletedImages || orderChanged) {
        await orderProfileImagesAPI({ orders: finalOrder });
      }

      await queryClient.invalidateQueries({
        queryKey: ProfileQueryKeys.getProfileCard('MY_PROFILE'),
      });
    } finally {
      setIsSaving(false);
    }
  }, [initialImages, queryClient]);

  const cleanup = useCallback(() => {
    localImagesRef.current.forEach((img) => {
      if (img.type === 'local') {
        URL.revokeObjectURL(img.previewUrl);
      }
    });
  }, []);

  return {
    localImages,
    isSaving,
    add,
    remove,
    swapImages,
    changeMain,
    submit,
    cleanup,
  };
};
